<template>
  <el-dialog
    id="suggestKeyResultsModal"
    top="5vh"
    :visible.sync="localVisible"
    width="720px"
    :close-on-click-modal="false"
    :append-to-body="true"
    :show-close="false"
    custom-class="suggest-kr-dialog"
    @close="handleClose"
  >
    <!-- Header -->
    <div slot="title" class="dialog-header">
      <div class="header-left">
        <div class="header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" fill="currentColor"/>
          </svg>
        </div>
        <div class="header-text">
          <h2 class="header-title">{{ $t("performance.goals.suggest_kr_title") }}</h2>
          <p class="header-subtitle">{{ $t("performance.goals.suggest_kr_subtitle") }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button
          v-if="!loading && hasSuggestions"
          class="refresh-btn"
          @click="handleRefresh"
          :disabled="loading"
          :title="$t('performance.goals.run_fresh_ai_analysis')"
        >
          <i class="el-icon-refresh"></i>
          {{ $t("performance.goals.refresh") }}
        </button>
        <button class="close-btn" @click="handleClose" aria-label="Close">
          <happily-icon name="x" :size="16" />
        </button>
      </div>
    </div>

    <div class="dialog-body">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-visual">
          <div class="loading-rings">
            <div class="ring ring-1"></div>
            <div class="ring ring-2"></div>
            <div class="ring ring-3"></div>
            <div class="ring-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="loading-content">
          <p class="loading-message">{{ currentLoadingMessage }}</p>
          <p class="loading-hint">{{ $t("performance.goals.kr_loading_hint") }}</p>
        </div>
        <div class="loading-steps">
          <div
            v-for="(step, idx) in loadingSteps"
            :key="idx"
            class="step"
            :class="{ 'active': idx === currentStepIndex, 'done': idx < currentStepIndex }"
          >
            <span class="step-dot"></span>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="el-icon-warning-outline"></i>
        </div>
        <p class="error-message">{{ error }}</p>
        <button class="retry-btn" @click="$emit('retry')">
          <i class="el-icon-refresh"></i>
          {{ $t("performance.goals.try_again") }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasSuggestions" class="empty-state">
        <div class="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="empty-title">{{ $t("performance.goals.no_suggestions_title") }}</h3>
        <p class="empty-desc">{{ $t("performance.goals.no_suggestions_desc") }}</p>
      </div>

      <!-- Results -->
      <div v-else class="results-container">
        <!-- Goal Context Banner -->
        <div class="goal-context-banner">
          <div class="banner-label">{{ $t("performance.goals.goal_context") }}</div>
          <div class="banner-title">{{ goalTitle }}</div>
          <div v-if="goalDescription" class="banner-description">{{ goalDescription }}</div>
        </div>

        <!-- Count Badge -->
        <div class="suggestions-count">
          <span class="count-badge">{{ suggestions.length }} suggestion{{ suggestions.length !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Suggestion Cards -->
        <div class="cards-list">
          <div
            v-for="(item, index) in suggestions"
            :key="index"
            class="suggestion-card"
            :class="{ 'accepted': item._accepted, 'skipped': item._skipped }"
          >
            <!-- Status: accepted indicator -->
            <div v-if="item._accepted" class="suggestion-status accepted">
              <i class="el-icon-check"></i> {{ $t("performance.goals.kr_added") }}
            </div>
            <!-- Status: skipped indicator -->
            <div v-else-if="item._skipped" class="suggestion-status skipped">
              {{ $t("performance.goals.kr_skipped") }}
            </div>

            <!-- Card content (when not yet processed) -->
            <template v-if="!item._accepted && !item._skipped">
              <!-- Title (editable) -->
              <el-input v-model="item._editTitle" size="small" class="suggestion-title-input" />

              <!-- Values row -->
              <div class="suggestion-details-row">
                <el-input-number v-model="item._editStartValue" size="mini" :min="0" class="suggestion-value-input" />
                <span class="suggestion-arrow">&rarr;</span>
                <el-input-number v-model="item._editTargetValue" size="mini" :min="0" class="suggestion-value-input" />
                <el-input v-model="item._editUnit" size="mini" :placeholder="$t('performance.goals.kr_unit')" class="suggestion-unit-input" style="width: 70px;" />
              </div>

              <!-- Confidence badge -->
              <div class="suggestion-meta">
                <span class="confidence-badge" :class="item.confidence">
                  {{ item.confidence }} {{ $t("performance.goals.kr_confidence") }}
                </span>
              </div>

              <!-- AI Reasoning (collapsible) -->
              <div class="suggestion-reasoning" v-if="item.reasoning">
                <button class="reasoning-toggle" @click="item._showReasoning = !item._showReasoning">
                  <i :class="item._showReasoning ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  {{ $t("performance.goals.kr_ai_reasoning") }}
                </button>
                <p v-if="item._showReasoning" class="reasoning-text">{{ item.reasoning }}</p>
              </div>

              <!-- Owner assignment -->
              <div class="suggestion-owner-row">
                <span class="suggestion-owner-label">{{ $t("performance.goals.kr_owner") }}</span>
                <el-select
                  v-model="item._editOwners"
                  size="mini"
                  multiple
                  filterable
                  clearable
                  :placeholder="$t('performance.goals.kr_assign_later')"
                  class="suggestion-owner-select"
                >
                  <el-option
                    v-for="member in teamMembers"
                    :key="member.email"
                    :label="member.fullname"
                    :value="member.email"
                  >
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <div
                        style="width: 20px; height: 20px; border-radius: 50%; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; color: white; flex-shrink: 0;"
                        :style="{
                          backgroundColor: member.house_color || '#ddd',
                          backgroundImage: member.large_pic ? 'url(' + member.large_pic + ')' : 'none',
                        }"
                      >
                        <span v-if="!member.large_pic">{{ getInitials(member.fullname) }}</span>
                      </div>
                      <span>{{ member.fullname }}</span>
                    </div>
                  </el-option>
                </el-select>
              </div>

              <!-- Actions -->
              <div class="suggestion-actions">
                <button class="skip-btn" @click="skipSuggestion(item)">{{ $t("performance.goals.kr_skip") }}</button>
                <button class="accept-btn" @click="acceptSuggestion(item)" :disabled="accepting">
                  <i v-if="accepting && acceptingIndex === suggestions.indexOf(item)" class="el-icon-loading"></i>
                  <template v-else><i class="el-icon-check"></i> {{ $t("performance.goals.kr_accept") }}</template>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'SuggestKeyResultsModal',
  props: {
    visible: { type: Boolean, default: false },
    goalId: { type: String, default: '' },
    teamId: { type: String, default: '' },
    goalTitle: { type: String, default: '' },
    goalDescription: { type: String, default: '' },
    existingKeyResults: { type: Array, default: () => [] },
    blurMode: { type: Boolean, default: false },
  },
  data() {
    return {
      localVisible: this.visible,
      loading: false,
      error: null,
      suggestions: [],
      accepting: false,
      acceptingIndex: -1,
      currentStepIndex: 0,
      stepInterval: null,
      loadingSteps: [
        'Analyzing goal context...',
        'Reviewing focus items...',
        'Generating measurable outcomes...',
        'Refining suggestions...',
      ],
    };
  },
  computed: {
    hasSuggestions() {
      return this.suggestions.length > 0;
    },
    currentLoadingMessage() {
      return this.loadingSteps[this.currentStepIndex % this.loadingSteps.length];
    },
    teamMembers() {
      if (!this.teamId) return [];
      return this.$store.getters['goals/teamMembersForGoal'](this.teamId) || [];
    },
  },
  watch: {
    visible(val) {
      this.localVisible = val;
      if (val) {
        this.loadOrFetch();
      }
    },
    localVisible(val) {
      if (!val) this.$emit('close');
    },
  },
  beforeDestroy() {
    this.stopLoadingAnimation();
  },
  methods: {
    async loadOrFetch() {
      this.error = null;
      this.suggestions = [];
      this.loading = true;
      this.startLoadingAnimation();

      try {
        // Try cache first
        const cached = await this.$store.dispatch('goals/loadCachedKrSuggestions', {
          teamId: this.teamId,
          goalId: this.goalId,
        });

        if (cached && cached.suggestedKeyResults && cached.suggestedKeyResults.length > 0) {
          this.processSuggestions(cached.suggestedKeyResults);
          this.loading = false;
          this.stopLoadingAnimation();
          return;
        }
      } catch (e) {
        // Cache miss, fetch fresh
      }

      // Fetch fresh from AI
      await this.fetchFresh();
    },

    async fetchFresh() {
      this.loading = true;
      this.startLoadingAnimation();

      try {
        // Invalidate old cache
        await this.$store.dispatch('goals/invalidateKrSuggestionsCache', {
          teamId: this.teamId,
          goalId: this.goalId,
        });
      } catch (e) {
        // Ignore cache invalidation errors
      }

      try {
        const result = await this.$store.dispatch('goals/suggestKeyResults', {
          teamId: this.teamId,
          goalId: this.goalId,
          goal: {
            goal_id: this.goalId,
            quick_preview: this.goalTitle,
            goal_description: this.goalDescription,
            existing_key_results: this.existingKeyResults,
          },
          focusItems: [],
          teamContext: { teamName: '', memberCount: 0 },
        });

        this.loading = false;
        this.stopLoadingAnimation();

        if (result && result.error) {
          this.error = result.error;
          return;
        }

        if (result && result.suggestedKeyResults) {
          this.processSuggestions(result.suggestedKeyResults);
        }
      } catch (e) {
        this.loading = false;
        this.stopLoadingAnimation();
        this.error = 'Failed to generate suggestions. Please try again.';
        console.error('Error fetching KR suggestions:', e);
      }
    },

    processSuggestions(rawSuggestions) {
      this.suggestions = rawSuggestions.map(s => ({
        ...s,
        _editTitle: s.title,
        _editStartValue: s.start_value || 0,
        _editTargetValue: s.target_value || 100,
        _editUnit: s.unit || '',
        _editOwners: [],
        _accepted: false,
        _skipped: false,
        _showReasoning: false,
      }));
    },

    async acceptSuggestion(item) {
      this.accepting = true;
      this.acceptingIndex = this.suggestions.indexOf(item);

      try {
        const keyResult = {
          kr_id: `kr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          title: item._editTitle.trim(),
          type: 'number',
          start_value: item._editStartValue,
          target_value: item._editTargetValue,
          current_value: item._editStartValue,
          unit: item._editUnit,
          owner_emails: item._editOwners || [],
          status: 'on_track',
          progress_history: [],
          created_at: new Date().toISOString(),
        };

        await this.$store.dispatch('goals/addKeyResult', {
          teamId: this.teamId,
          goalId: this.goalId,
          keyResult,
        });

        item._accepted = true;
        this.$emit('accepted');
      } catch (e) {
        console.error('Error accepting KR suggestion:', e);
      } finally {
        this.accepting = false;
        this.acceptingIndex = -1;
      }
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return (parts[0][0] || '?').toUpperCase();
    },

    skipSuggestion(item) {
      item._skipped = true;
    },

    handleRefresh() {
      this.suggestions = [];
      this.error = null;
      this.fetchFresh();
    },

    handleClose() {
      this.localVisible = false;
      this.stopLoadingAnimation();
    },

    startLoadingAnimation() {
      this.currentStepIndex = 0;
      if (this.stepInterval) {
        clearInterval(this.stepInterval);
      }
      this.stepInterval = setInterval(() => {
        this.currentStepIndex = (this.currentStepIndex + 1) % 4;
      }, 4000);
    },

    stopLoadingAnimation() {
      if (this.stepInterval) {
        clearInterval(this.stepInterval);
        this.stepInterval = null;
      }
    },
  },
};
</script>

<style scoped>
/* Dialog Overrides */
.suggest-kr-dialog .el-dialog__header {
  padding: 0;
}
.suggest-kr-dialog .el-dialog__body {
  padding: 0;
}

/* Header */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #ebeef5;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(108, 48, 204, 0.08);
  color: #6C30CC;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-text {
  display: flex;
  flex-direction: column;
}
.header-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #303133;
  line-height: 1.3;
}
.header-subtitle {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.3;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(108, 48, 204, 0.06);
  border: 1px solid rgba(108, 48, 204, 0.2);
  border-radius: 8px;
  color: #6C30CC;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}
.refresh-btn:hover {
  background: rgba(108, 48, 204, 0.12);
}
.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  color: #909399;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}
.close-btn:hover {
  color: #303133;
  border-color: #c0c4cc;
}

/* Body */
.dialog-body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
}
.loading-visual {
  margin-bottom: 24px;
}
.loading-rings {
  position: relative;
  width: 100px;
  height: 100px;
}
.ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid #6C30CC;
}
.ring-1 {
  width: 100px;
  height: 100px;
  top: 0;
  left: 0;
  opacity: 0.3;
  animation: ring-pulse-1 3s ease-in-out infinite;
}
.ring-2 {
  width: 76px;
  height: 76px;
  top: 12px;
  left: 12px;
  opacity: 0.2;
  animation: ring-pulse-2 3s ease-in-out infinite 0.5s;
}
.ring-3 {
  width: 52px;
  height: 52px;
  top: 24px;
  left: 24px;
  opacity: 0.15;
  animation: ring-pulse-3 3s ease-in-out infinite 1s;
}
.ring-center {
  position: absolute;
  width: 36px;
  height: 36px;
  top: 32px;
  left: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6C30CC;
}
.loading-content {
  text-align: center;
  margin-bottom: 16px;
}
.loading-message {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 4px;
}
.loading-hint {
  font-size: 12px;
  color: #909399;
  margin: 0;
}
.loading-steps {
  display: flex;
  gap: 8px;
}
.step .step-dot {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.3s;
}
.step.active .step-dot {
  background: #6C30CC;
  transform: scale(1.25);
}
.step.done .step-dot {
  background: #3AC47D;
}

@keyframes ring-pulse-1 {
  0%, 100% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.1); opacity: 0.6; }
}
@keyframes ring-pulse-2 {
  0%, 100% { transform: scale(1); opacity: 0.2; }
  50% { transform: scale(1.15); opacity: 0.5; }
}
@keyframes ring-pulse-3 {
  0%, 100% { transform: scale(1); opacity: 0.15; }
  50% { transform: scale(1.2); opacity: 0.4; }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
}
.error-icon {
  font-size: 48px;
  color: #F7B924;
  margin-bottom: 16px;
}
.error-message {
  font-size: 14px;
  color: #606266;
  margin: 0 0 16px;
  text-align: center;
}
.retry-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #6C30CC;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}
.retry-btn:hover {
  background: #5a28a8;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
}
.empty-icon {
  color: #3AC47D;
  margin-bottom: 16px;
}
.empty-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}
.empty-desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
  text-align: center;
  max-width: 400px;
  line-height: 1.5;
}

/* Results Container */
.results-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Goal Context Banner */
.goal-context-banner {
  background: rgba(108, 48, 204, 0.04);
  border: 1px solid rgba(108, 48, 204, 0.12);
  border-radius: 4px;
  padding: 12px;
}
.banner-label {
  font-size: 11px;
  font-weight: 600;
  color: #6C30CC;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.banner-title {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 2px;
}
.banner-description {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}

/* Suggestions Count */
.suggestions-count {
  display: flex;
  align-items: center;
}
.count-badge {
  font-size: 12px;
  font-weight: 600;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.08);
  padding: 2px 10px;
  border-radius: 10px;
}

/* Suggestion Cards */
.cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.suggestion-card {
  background: white;
  border: 1px solid #eef0f5;
  border-radius: 4px;
  padding: 16px;
  transition: border-color 0.3s;
}
.suggestion-card:hover {
  border-color: #c0c4cc;
}
.suggestion-card.accepted {
  border-color: #3AC47D;
  background: rgba(58, 196, 125, 0.03);
}
.suggestion-card.skipped {
  opacity: 0.5;
  border-color: #eef0f5;
}

/* Suggestion Status */
.suggestion-status {
  font-size: 13px;
  font-weight: 600;
  padding: 8px 0;
  text-align: center;
}
.suggestion-status.accepted {
  color: #3AC47D;
}
.suggestion-status.skipped {
  color: #909399;
}

/* Title Input */
.suggestion-title-input {
  margin-bottom: 12px;
}
.suggestion-title-input >>> .el-input__inner {
  font-weight: 600;
  font-size: 13px;
}

/* Details Row */
.suggestion-details-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.suggestion-type-select {
  width: 120px;
}
.suggestion-value-input {
  width: 100px;
}
.suggestion-arrow {
  color: #909399;
  font-size: 14px;
  flex-shrink: 0;
}
.suggestion-unit-input >>> .el-input__inner {
  font-size: 12px;
}

/* Confidence Badges */
.suggestion-meta {
  margin-bottom: 8px;
}
.confidence-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;
}
.confidence-badge.high {
  background: rgba(58, 196, 125, 0.12);
  color: #3AC47D;
}
.confidence-badge.medium {
  background: rgba(251, 192, 45, 0.12);
  color: #FBC02D;
}
.confidence-badge.low {
  background: rgba(144, 147, 153, 0.12);
  color: #909399;
}

/* Reasoning */
.suggestion-reasoning {
  margin-bottom: 10px;
}
.reasoning-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  transition: color 0.3s;
}
.reasoning-toggle:hover {
  color: #606266;
}
.reasoning-text {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin: 6px 0 0;
  padding-left: 4px;
}

/* Actions */
.suggestion-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
.skip-btn {
  background: white;
  border: 1px solid #c9c9c9;
  border-radius: 8px;
  padding: 6px 16px;
  color: #747474;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}
.skip-btn:hover {
  border-color: #909399;
  color: #606266;
}
.accept-btn {
  background: #6C30CC;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.3s;
}
.accept-btn:hover {
  background: #5a28a8;
}
.accept-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Owner Select */
.suggestion-owner-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.suggestion-owner-label {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  flex-shrink: 0;
}

.suggestion-owner-select {
  flex: 1;
  max-width: 240px;
}
</style>
